import React, { useCallback, useMemo } from 'react'
import { FormAutosuggest, FormTextField, useWatch, useZionFormContext } from '@fs/zion-form'
import { ContentAdd, ContentDelete } from '@fs/zion-icon'
import {
  Button,
  Cell,
  Checkbox,
  CheckboxGroup,
  FlowGrid,
  IconButton,
  LabelSpacer,
  MenuOverlay,
  Paragraph,
  Row,
  Separator,
} from '@fs/zion-ui'
import {
  useSlsStateContext,
  getSlsTypesByKey,
  useSlsHighlightsContext,
  getDisplayLabelForFieldType,
  getNestedOrFloatingElementById,
} from '@fs/zion-sls'
import { useTranslation } from 'react-i18next'
import { useFeatureFlag } from '@fs/zion-flags'
import { sortNamePartTypes } from '../utils/helpers'
import { namePartTypeKeys } from '../../shared/utils/helpers'
import useNameParts from '../hooks/useNameParts'
import { handleInputFocus, handleInputBlur } from '../../shared/utils/highlightUtils'
import HighlightButton from '../../panels/shared/HighlightButton'
import LinkNamesButton from '../../panels/shared/LinkNamesButton'
import { useLinkNamesSave } from '../../panels/linkNamesPanel/hooks/useLinkNamesSave'

/**
 * NamePartsForm component for managing and displaying editable name parts within the EssentialInformationForm component.
 *
 * This component allows users to add, remove, and edit various parts of a name (e.g., first name, last name, middle name).
 * It integrates with the useFieldArray hook for form state management and SLS for type definitions and highlights.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Object} props.field - The field object representing the current name entry.
 * @param {number} props.index - The index of the name entry in the names array.
 * @param {Object} props.fieldArrayMethods - Methods for manipulating the field array (add, remove, etc). The results of useFieldArray hook.
 * @param {boolean} [props.hideHeader=false] - When true, suppresses the primary / alternative name header label region.
 * @param {boolean} [props.hideHighlightButton=false] - When true, omits the per-field highlight button column.
 * @param {Object} props.person - The person object associated with the name parts.
 * @param {string} props.splitScreenImageArk - The ARK ID of the image used for split-screen highlighting.
 *
 * @returns {JSX.Element} The rendered NamePartsForm component.
 */
const NamePartsForm = ({
  field,
  index,
  fieldArrayMethods,
  hideHeader = false,
  hideHighlightButton = false,
  splitScreenImageArk,
  person,
}) => {
  const [t] = useTranslation()
  const { types: slsTypes, images, currentImageArk } = useSlsStateContext()
  const h = useSlsHighlightsContext()
  const formMethods = useZionFormContext()
  const { getValues, formState, resetField } = formMethods
  const rip_sharedImageViewer_fullTextAttachFlow_v2 = useFeatureFlag('rip_sharedImageViewer_fullTextAttachFlow_v2')
  const personId = person?.id

  // Getting SLS types for names and fields only once
  const slsNameTypes = useMemo(() => getSlsTypesByKey(slsTypes, 'names'), [slsTypes])
  const slsFieldTypes = useMemo(() => getSlsTypesByKey(slsTypes, 'fields'), [slsTypes])

  // Filter SLS field types to only include those relevant for name parts
  const nameFieldTypes = useMemo(
    () =>
      slsFieldTypes
        .filter((type) => namePartTypeKeys.includes(type.fieldType))
        .sort((a, b) => sortNamePartTypes(a, b, 'fieldType')),
    [slsFieldTypes]
  )

  // Prepare options for the name type autosuggest
  const nameTypeAutosuggestOptions = useMemo(
    () => slsNameTypes.map((type) => ({ primaryText: type.displayLabel, key: type.nameType })),
    [slsNameTypes]
  )

  // We watch both 'parts' and 'removedParts' to re-render when a part is added or removed
  const [selectedNamePartTypes] = useWatch({
    name: [`names.${index}.parts`],
  })

  // Determine which name parts are currently active (not removed) in the form
  const formNamePartFieldIdentifiers = selectedNamePartTypes?.map((type) => `names.${index}.${type}`) || []
  const currentNamePartFieldsInForm = getValues(formNamePartFieldIdentifiers)
  const activeNameParts = currentNamePartFieldsInForm
    ?.filter((namePart) => namePart && !namePart?.removed)
    .sort(sortNamePartTypes)
  const activeNamePartTypes = activeNameParts?.map((part) => part.type) || []
  const isNewPerson = formState.defaultValues?.added ?? true

  // Handlers from the custom hook to manage name parts
  const { handleNamePartToggle, handleRemoveName } = useNameParts({ index, formMethods, fieldArrayMethods })

  // Handlers to manage highlight hover on focus/blur of input fields - using utility functions
  const onInputFocus = useCallback(
    (fieldType) => handleInputFocus(`names.${index}.${fieldType}.id`, getValues, h),
    [getValues, h, index]
  )

  const onInputBlur = useCallback(() => handleInputBlur(h), [h])

  // Use the hook for handling link names save
  const { handleLinkNamesSave } = useLinkNamesSave({
    getValues,
    resetField,
    images,
    currentImageArk,
  })

  // Prepares a field object for use in the HighlightButtonOld component
  const prepareFieldForHighlights = useCallback(
    (type) => {
      const currentField = getValues(`names.${index}.${type}`)
      const existingSlsField = getNestedOrFloatingElementById(images, currentField?.id)

      return {
        ...currentField,
        displayLabel: getDisplayLabelForFieldType(type, slsTypes),
        name: `names.${index}.${type}.value`,
        fieldType: type,
        elementType: 'FIELD',
        superElements: [
          {
            partitionKey: currentImageArk,
            id: getValues(`names.${index}.${type}.elementId`),
          },
        ],
        personId: personId || existingSlsField?.personId, // Use personId from props or fallback to SLS data if available
        ...existingSlsField, // Spread existing SLS data
      }
    },
    [images, slsTypes, currentImageArk, index, personId, getValues]
  )

  const handleHighlightSave = useCallback(
    (fieldName) => (data) => {
      if (data?.newFieldId && data?.newFieldValue) {
        const currentFieldData = getValues(fieldName)
        const linkedPersonIds =
          data?.selectedPersonIds?.map((id) => ({
            partitionKey: currentImageArk,
            id,
          })) || []

        resetField(fieldName, {
          defaultValue: {
            ...currentFieldData,
            id: data.newFieldId,
            value: data.newFieldValue,
            elementId: data?.newElementId ?? currentFieldData?.elementId, // If a new element was created, use that ID. Otherwise, keep existing elementId
            removed: false,
            dispatches: data?.dispatches || [],
            shared: currentFieldData?.shared || data?.selectedPersonIds?.length > 1, // Share the field if it's associated with multiple persons
            linkedPersonIds,
          },
          keepDirty: false,
          keepErrors: false,
          keepTouched: false,
        })
      }
    },
    [getValues, resetField, currentImageArk]
  )

  return (
    <FlowGrid orientation="vertical" columnCount={1} gutters="nano" key={field.id}>
      {index < 2 && !hideHeader && (
        <Paragraph size="sm">
          <strong>
            {index === 0 && t('essentialInformationForm.names.primaryName', 'Primary Name')}
            {index === 1 && t('essentialInformationForm.names.alternativeNames', 'Alternative Names')}
          </strong>
        </Paragraph>
      )}

      <Row gutters="xxs">
        <FormAutosuggest
          name={`names.${index}.type`}
          label={t('essentialInformationForm.names.nameType', 'Name Type')}
          suggestions={nameTypeAutosuggestOptions}
          listMaxHeight={500}
          data-testid={`name-type-autosuggest-${index}`}
          dense
          growX
          warningValidate={() => {
            // Check for validation errors on this specific name type field
            // Handle both React Hook Form structure and custom fieldArray names array structures
            const nameTypeError = formState.errors?.names?.[index]?.type
            const customValidationError = formState.errors?.names?.[index]

            if (nameTypeError?.message) {
              return nameTypeError.message
            }

            if (customValidationError?.message && typeof customValidationError.message === 'string') {
              return customValidationError.message
            }

            return null
          }}
        />
        {index > 0 && (
          <Cell>
            <LabelSpacer />
            <IconButton
              Icon={ContentDelete}
              title={t('essentialInformationForm.names.removeName', 'Remove Name')}
              onClick={() => handleRemoveName(index)}
              color="red"
              data-testid={`remove-name-button-${index}`}
              linkName="essential-information-remove-name"
            />
          </Cell>
        )}
      </Row>

      {activeNameParts.map((visibleNamePart) => (
        <React.Fragment key={visibleNamePart.type}>
          <Row gutters="xxs" key={visibleNamePart.type}>
            <FormTextField
              name={`names.${index}.${visibleNamePart.type}.value`}
              label={getDisplayLabelForFieldType(visibleNamePart.type, slsTypes)}
              onFocus={() => onInputFocus(visibleNamePart.type)}
              onBlur={onInputBlur}
              data-testid={`name-${index}-${visibleNamePart.type}-field`}
              warningValidate={(value) => {
                const originalValue = formState.defaultValues?.names?.[index]?.[visibleNamePart.type]?.value || ''
                const isSharedField = visibleNamePart.shared

                if (value !== originalValue && isSharedField && !isNewPerson) {
                  return t(
                    'essentialInformationForm.names.shared-field-change-warning',
                    'Changes will be made on all who are linked.'
                  )
                }

                return null
              }}
              dense
              growX
            />
            {!hideHighlightButton && (
              <Cell>
                <LabelSpacer />
                <HighlightButton
                  field={prepareFieldForHighlights(visibleNamePart.type)}
                  splitScreenImageArk={splitScreenImageArk}
                  buttonProps={{
                    'data-testid': `name-${index}-${visibleNamePart.type}-highlight-button`,
                  }}
                  onSave={handleHighlightSave(`names.${index}.${visibleNamePart.type}`)}
                  saveToSls={!isNewPerson && !field?.added} // Only save to SLS if this is not a new person and the field isn't newly added (i.e., it already exists in SLS)
                  growX
                  iconOnly
                />
              </Cell>
            )}
          </Row>
          {rip_sharedImageViewer_fullTextAttachFlow_v2.isOn && visibleNamePart.shared && !isNewPerson && (
            <LinkNamesButton
              person={person}
              field={visibleNamePart}
              onSave={handleLinkNamesSave(`names.${index}.${visibleNamePart.type}`)}
              linkedPersonIds={visibleNamePart.linkedPersonIds || []}
              disabled={visibleNamePart.added}
            />
          )}
        </React.Fragment>
      ))}

      <>
        <Separator size="nano" />
        <MenuOverlay
          menuButton={
            <Button
              Icon={ContentAdd}
              data-testid={`name-${index}-more-info-button`}
              linkName="essential-information-more-info"
              dense
            >
              {t('essentialInformationForm.names.moreInfo', 'More Info')}
            </Button>
          }
          aria-label={t('essentialInformationForm.names.selectNameFields', 'Select name fields to add')}
        >
          <Separator size="nano" />
          <CheckboxGroup
            label={t('essentialInformationForm.names.selectNameFields', 'Select name fields to add')}
            dense
          >
            {nameFieldTypes.sort(sortNamePartTypes).map((nameFieldType) => (
              <React.Fragment key={nameFieldType.fieldType}>
                <Checkbox
                  key={nameFieldType.fieldType}
                  name={`names.${index}.parts`}
                  value={nameFieldType.fieldType}
                  label={nameFieldType.displayLabel}
                  checked={activeNamePartTypes?.includes(nameFieldType.fieldType)}
                  onChange={(event) => handleNamePartToggle(event, nameFieldType.fieldType)}
                  data-testid={`name-part-${index}-${nameFieldType.fieldType}-checkbox`}
                  role="menuitemcheckbox"
                />
                <Separator size="nano" />
              </React.Fragment>
            ))}
          </CheckboxGroup>
        </MenuOverlay>
      </>
    </FlowGrid>
  )
}

export default NamePartsForm
